import React, { useState, useContext, useRef, useEffect } from "react";
import { AuthContext } from "@/App";
import { useLocation } from "react-router-dom";
import { HelpCircle, X, Send, ChevronRight, ArrowLeft } from "lucide-react";

const playerFaqs = [
  {
    id: "enter-scores",
    question: "How do I enter my scores?",
    keywords: ["enter", "score", "scores", "submit", "round", "input", "record", "add"],
    answer: "From your Dashboard, tap **'Enter Score'** on the competition row. The date defaults to today (within the comp range). Enter your course handicap, then use the **+/\u2013 buttons** to set each hole's score. Tap **'Review & Submit'** and confirm.",
    category: "Scoring"
  },
  {
    id: "first-round",
    question: "What do I do for my first round?",
    keywords: ["first", "round", "baseline", "start", "beginning", "new"],
    answer: "Your first round sets your **baseline**. You must enter a score for every hole. After that, subsequent rounds only need scores for holes where you **improved** (scored lower). Blank holes keep your current best.",
    category: "Scoring"
  },
  {
    id: "view-best",
    question: "Where can I see my best scores?",
    keywords: ["best", "scores", "view", "see", "check", "look", "summary", "my scores"],
    answer: "Tap on a competition from your Dashboard, then tap **'View Best Scores'**. This shows your best score for each hole across all your rounds, plus your total gross and net scores.",
    category: "Scores"
  },
  {
    id: "join-competition",
    question: "How do I join a competition?",
    keywords: ["join", "competition", "register", "sign up", "enter competition", "enrol"],
    answer: "From your Dashboard, scroll to **'Open to join'** and tap **'Join'** on the competition you want. Once joined, it moves up to **'Your competitions'** and you can start entering rounds.",
    category: "Getting Started"
  },
  {
    id: "what-is-eclectic",
    question: "What is an eclectic competition?",
    keywords: ["eclectic", "what is", "explain", "how does it work", "format", "concept"],
    answer: "An eclectic records your **best score on each hole** across multiple rounds. Play several rounds and your best individual hole scores combine into one 'dream round'. The winner has the lowest combined best scores.",
    category: "Getting Started"
  },
  {
    id: "colors-meaning",
    question: "What do the colours on the score cards mean?",
    keywords: ["colour", "color", "green", "red", "yellow", "border", "highlight", "mean", "indicator"],
    answer: "**Green border** = you've improved on that hole (lower than your best). **Red border** = score is worse than your current best. **Yellow highlight** = hole-in-one. **Green highlight** = Excellent 2 (score of 2). **Grey** = first round, no comparison yet.",
    category: "Scoring"
  },
  {
    id: "edit-round",
    question: "How do I edit a round I've already entered?",
    keywords: ["edit", "change", "correct", "mistake", "wrong", "update", "modify", "amend"],
    answer: "Open the competition and tap **'View Best Scores'**, then the **'Rounds'** tab. Find the round to change and tap the **Edit icon**. You can update the date, handicap, or scores.",
    category: "Scores"
  },
  {
    id: "handicap",
    question: "What is the course handicap?",
    keywords: ["handicap", "course handicap", "hcp", "what handicap", "net"],
    answer: "Your **course handicap** is the number of strokes you receive for that specific course. Enter it when submitting each round. It's used to calculate your **net score** (gross minus half your handicap).",
    category: "Scoring"
  },
  {
    id: "leaderboard",
    question: "How do I see the leaderboard?",
    keywords: ["leaderboard", "standings", "ranking", "position", "where do i stand", "leader"],
    answer: "From your competition's **'View Best Scores'** page, tap the **'Leaderboard'** tab. You'll see all players ranked by their net eclectic scores. You can also download it as a PDF.",
    category: "Scores"
  },
  {
    id: "withdraw",
    question: "How do I withdraw from a competition?",
    keywords: ["withdraw", "leave", "quit", "drop out", "exit", "remove myself"],
    answer: "On your Dashboard, tap the **three-dot menu (\u22EE)** next to the competition's **Enter Score** button and choose **'Withdraw from competition'**. You'll be asked to type **WITHDRAW** to confirm — a safety measure so nobody withdraws by accident. Your scores are preserved for **7 days** in case you change your mind (see: How do I rejoin?).",
    category: "Getting Started"
  },
  {
    id: "rejoin-after-withdraw",
    question: "I withdrew by mistake — how do I rejoin?",
    keywords: ["rejoin", "undo withdraw", "come back", "restore", "get back in", "reinstate"],
    answer: "If you withdrew within the last **7 days**, a **'Rejoin'** button appears on the Dashboard where the Enter Score button used to be. Tap it and all your scores are restored. After 7 days, ask your club admin to add you back.",
    category: "Getting Started"
  },
  {
    id: "club-access-code",
    question: "What's a club access code?",
    keywords: ["access code", "invite code", "signup code", "club code", "signing up", "register", "join club"],
    answer: "New sign-ups need a shared **Club Access Code** from your admin. If you enter it correctly at signup, you're in immediately. Without it, your account is created but an admin has to approve you before you can log in.",
    category: "Getting Started"
  },
  {
    id: "one-round-per-day",
    question: "Can I enter more than one round per day?",
    keywords: ["one round", "per day", "daily", "multiple rounds", "same day", "already entered", "duplicate"],
    answer: "No \u2014 you can only enter **one round per day** per competition. This is enforced by the database, so even if you accidentally hit Submit twice, only one round is saved. If you need to correct a score, use **Edit** on your existing round.",
    category: "Scoring"
  },
  {
    id: "selective-entry",
    question: "Do I have to enter all 18 holes every time?",
    keywords: ["selective", "partial", "some holes", "improved", "only changed", "blank", "skip"],
    answer: "Only on your **first round** do you need to enter all holes. After that, you only enter scores for holes where you **improved**. Leave the others blank \u2014 they'll automatically keep your current best score.",
    category: "Scoring"
  },
  {
    id: "excellent-2",
    question: "What is an Excellent 2?",
    keywords: ["excellent", "diamond", "score of 2", "two", "celebration", "achievement"],
    answer: "When a competition has **'Recognize Score of 2'** enabled, scoring a 2 on any hole triggers a celebration. You'll be asked to confirm, and a congratulatory message is posted to the competition feed.",
    category: "Scoring"
  },
  {
    id: "hole-in-one",
    question: "What happens if I get a hole-in-one?",
    keywords: ["hole in one", "hole-in-one", "ace", "score of 1", "one"],
    answer: "Score a 1 on any hole and you'll get a **confirmation dialog**. If confirmed, a celebration message is posted and the hole is highlighted in **yellow** with a trophy icon on your scorecard.",
    category: "Scoring"
  },
  {
    id: "9-hole",
    question: "How do 9-hole competitions work?",
    keywords: ["9 hole", "nine hole", "front 9", "back 9", "half", "9-hole"],
    answer: "Some competitions only use **Front 9** (holes 1\u20139) or **Back 9** (holes 10\u201318). You'll only see and enter scores for the relevant holes. Everything else works the same as 18-hole competitions.",
    category: "Getting Started"
  },
  {
    id: "social-feed",
    question: "What is the social feed?",
    keywords: ["social", "feed", "messages", "posts", "news", "announcements", "chat"],
    answer: "The social feed shows competition announcements, hole-in-one celebrations, and Excellent 2 achievements. You can view messages from the **Messages** section in your competition view.",
    category: "Getting Started"
  },
  {
    id: "date-picker-default",
    question: "Why does the date picker open on today?",
    keywords: ["date", "picker", "calendar", "today", "default", "when"],
    answer: "The date picker now defaults to **today's date** (clamped to the competition range), so you don't have to scroll through months. Tap the field to open the calendar and choose a different date if needed.",
    category: "Scoring"
  }
];

const adminFaqs = [
  {
    id: "admin-create-comp",
    question: "How do I create a competition?",
    keywords: ["create", "new", "competition", "set up", "setup", "start"],
    answer: "From the Admin Dashboard tap **'Create Competition'** (top-right). Set the name, dates, total rounds, hole format (18/Front 9/Back 9), and any optional features (Excellent 2, hole-in-one recognition, etc.).",
    category: "Admin"
  },
  {
    id: "admin-add-player",
    question: "How do I add a player to a competition?",
    keywords: ["add player", "register player", "invite", "player to competition"],
    answer: "Open the competition (tap **View** on the comp row), then tap **'Add Player'**. Enter the player's email. If they don't have an account yet, use **'Create & Add Player'** to create one for them.",
    category: "Admin"
  },
  {
    id: "admin-enter-score",
    question: "How do I enter scores for a player?",
    keywords: ["enter score", "admin score", "on behalf", "player score", "submit for"],
    answer: "Open the competition, find the player row, and tap **'Enter Score'**. Enter the date, handicap, and use the +/\u2013 buttons per hole. The system auto-detects hole-in-ones and Excellent 2s.",
    category: "Admin"
  },
  {
    id: "admin-pdf",
    question: "How do I download the leaderboard as a PDF?",
    keywords: ["pdf", "download", "leaderboard", "print", "export", "report"],
    answer: "Open the competition and tap **'Leaderboard PDF'** in the Downloads & Management panel. The file downloads to your browser's Downloads folder.",
    category: "Admin"
  },
  {
    id: "admin-audit-log",
    question: "How do I see who changed what (audit log)?",
    keywords: ["audit", "log", "history", "who changed", "who edited", "changes", "track", "changes made", "player changes", "player activity"],
    answer: "There are TWO ways to see the audit log:\n\n1. **Whole competition** — On the Admin Dashboard, in the row for the competition, tap the **Shield icon** labelled 'Audit' (a red dot appears when there have been changes in the last 24h). Filter by user role, action, date range, search by player name, and export to Excel.\n\n2. **Single player** — Open the competition (tap 'View' on the row), find the player's row, and tap the **'Activity' button** (also a Shield icon). The audit log opens pre-filtered to just that player's actions.",
    category: "Admin"
  },
  {
    id: "admin-delete-player",
    question: "How do I delete a player (including Google sign-ups)?",
    keywords: ["delete player", "remove player", "unknown player", "google player", "delete user", "remove user", "bogus signup"],
    answer: "From the admin nav go to **Player Management**. Find the player and tap the **red bin icon** on their row. This works for both Email/Password and Google-authenticated accounts and removes them from ALL competitions with all their data. If you want to remove them only from one competition, open the competition and use the X icon on their row instead.",
    category: "Admin"
  },
  {
    id: "admin-audit-purge",
    question: "How do I clear old audit log entries?",
    keywords: ["purge", "clear audit", "delete audit", "old logs", "database space"],
    answer: "Open the Audit Log for a competition and tap **'Purge logs'**. This is only enabled **after the competition end date** — that keeps live comps immutable but lets you free up DB space once a comp is concluded and there are no disputes.",
    category: "Admin"
  },
  {
    id: "admin-access-code",
    question: "How do I set the club access code?",
    keywords: ["access code", "signup code", "club code", "invite code", "signup control"],
    answer: "Tap the **Shield/Access** button in the top-right of the admin navigation. Set or rotate the shared club access code — members who enter it correctly at signup are auto-approved. Anyone who signs up without it goes into the approval queue.",
    category: "Admin"
  },
  {
    id: "admin-approve-pending",
    question: "How do I approve pending sign-ups?",
    keywords: ["approve", "pending", "sign up", "signup queue", "new users", "reject", "waiting"],
    answer: "Tap the **Access** button in the admin nav. Any signup that didn't use the club access code shows here — tap **Approve** (green) or **Reject** (red). Approved users get emailed and can log in immediately.",
    category: "Admin"
  },
  {
    id: "admin-certificates",
    question: "How do I send certificates?",
    keywords: ["certificate", "certificates", "award", "send certificates", "email certificates"],
    answer: "Open the competition and tap **'Top 3 Certificates'** to preview, or **'Email Top 3'** to send them directly. Certificates use your club branding and are attached as PDFs.",
    category: "Admin"
  },
  {
    id: "admin-bulk",
    question: "How does bulk entry work?",
    keywords: ["bulk", "multiple rounds", "batch", "many rounds", "bulk entry"],
    answer: "Open a player's row in the competition and tap **'Bulk Entry'**. Add multiple rounds at once in a spreadsheet-style view — great for catching a player up after a break.",
    category: "Admin"
  },
  {
    id: "admin-edit-round",
    question: "How do I edit a player's round?",
    keywords: ["edit round", "change round", "correct", "modify", "update player", "wrong score"],
    answer: "Open the competition, find the player row, and tap **'View Rounds'**. Tap the **Edit icon** on the round you want to change. Every edit is recorded in the audit log.",
    category: "Admin"
  },
  {
    id: "admin-remove-player",
    question: "How do I remove a player who withdrew and doesn't want back in?",
    keywords: ["remove player", "delete player", "kick", "expel", "hard delete", "clean up"],
    answer: "Open the competition, find the player row, and use the **X / Remove** action. This hard-deletes their registration + rounds + best scores (audited). Prefer waiting for their 7-day self-rejoin window to expire if they might come back.",
    category: "Admin"
  },
  {
    id: "admin-email",
    question: "How do I email players?",
    keywords: ["email", "message", "notify", "send email", "communicate", "contact"],
    answer: "Use **'Email Players'** from the admin nav. You can send messages to all players or specific groups. Competition-specific messages can be posted from the competition's message area.",
    category: "Admin"
  },
  {
    id: "admin-broadcast",
    question: "How do I send an in-app broadcast?",
    keywords: ["broadcast", "announcement", "notify all", "app message", "in-app"],
    answer: "Tap the **Broadcast** button in the top-right of the admin nav. Set a title, content, and priority — the message appears in every player's in-app inbox (respecting their notification preferences).",
    category: "Admin"
  }
];

function matchFaqs(query, faqs) {
  if (!query || query.trim().length < 2) return [];
  
  const words = query.toLowerCase().split(/\s+/).filter(w => w.length > 1);
  
  const scored = faqs.map(faq => {
    let score = 0;
    const qLower = query.toLowerCase();
    
    // Check question similarity
    if (faq.question.toLowerCase().includes(qLower)) score += 10;
    
    // Check keyword matches
    for (const keyword of faq.keywords) {
      if (qLower.includes(keyword)) score += 5;
      for (const word of words) {
        if (keyword.includes(word) || word.includes(keyword)) score += 2;
      }
    }
    
    // Check answer content
    const ansLower = faq.answer.toLowerCase();
    for (const word of words) {
      if (ansLower.includes(word)) score += 1;
    }
    
    return { ...faq, score };
  });
  
  return scored.filter(f => f.score > 2).sort((a, b) => b.score - a.score).slice(0, 3);
}

function formatAnswer(text) {
  // Convert **bold** to <strong> tags
  return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
}

export default function HelpBot() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [showTopics, setShowTopics] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Don't show on landing/login pages
  const hiddenPaths = ["/", "/login", "/register", "/admin/login", "/forgot-password", "/reset-password"];
  if (!user || hiddenPaths.includes(location.pathname)) return null;

  const isAdmin = user.role === "admin";
  const allFaqs = isAdmin ? [...playerFaqs, ...adminFaqs] : playerFaqs;
  
  const quickTopics = isAdmin
    ? [
        { label: "Approve pending sign-ups", query: "approve pending" },
        { label: "Set the club access code", query: "access code" },
        { label: "See the audit log", query: "audit log" },
        { label: "Enter scores for a player", query: "enter scores for player" },
        { label: "Create a competition", query: "create competition" },
        { label: "Download leaderboard PDF", query: "download pdf" },
        { label: "Send certificates", query: "certificates" },
        { label: "Bulk entry", query: "bulk entry" },
      ]
    : [
        { label: "How do I enter my scores?", query: "enter scores" },
        { label: "How do I withdraw?", query: "withdraw" },
        { label: "I withdrew — how do I rejoin?", query: "rejoin" },
        { label: "Where are my best scores?", query: "best scores" },
        { label: "How do I edit a round?", query: "edit round" },
        { label: "What is an eclectic?", query: "what is eclectic" },
      ];

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleSearch = (searchQuery) => {
    const q = searchQuery || query;
    if (!q.trim()) return;

    // Add user message
    const userMsg = { type: "user", text: q };
    const results = matchFaqs(q, allFaqs);
    
    let botMsg;
    if (results.length > 0) {
      botMsg = {
        type: "bot",
        results: results,
      };
    } else {
      botMsg = {
        type: "bot",
        text: "I couldn't find a match for that. Try asking about **entering scores**, **viewing best scores**, **editing rounds**, or **joining competitions**. You can also check the full guide from your Dashboard.",
      };
    }

    setMessages(prev => [...prev, userMsg, botMsg]);
    setQuery("");
    setShowTopics(false);
    scrollToBottom();
  };

  const handleTopicClick = (topicQuery) => {
    handleSearch(topicQuery);
  };

  const handleReset = () => {
    setMessages([]);
    setShowTopics(true);
    setQuery("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          data-testid="help-bot-button"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-ink hover:bg-ink-hover text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group"
          aria-label="Help"
        >
          <HelpCircle className="w-7 h-7 group-hover:scale-110 transition-transform" />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div
          data-testid="help-bot-panel"
          className="fixed bottom-5 right-5 z-50 w-[360px] max-w-[calc(100vw-40px)] h-[520px] max-h-[calc(100vh-100px)] bg-white rounded-2xl shadow-2xl border border-line flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-ink text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <button onClick={handleReset} className="hover:bg-accent-tint-20 p-1 rounded transition-colors">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              <HelpCircle className="w-5 h-5" />
              <span className="font-semibold text-sm">Help Assistant</span>
            </div>
            <button
              data-testid="help-bot-close"
              onClick={() => setIsOpen(false)}
              className="hover:bg-accent-tint-20 p-1 rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {/* Welcome + Topics */}
            {showTopics && messages.length === 0 && (
              <>
                <div className="bg-accent-tint-2 border border-line rounded-xl p-3">
                  <p className="text-sm text-ink font-medium mb-1">
                    Hi{user.name ? ` ${user.name.split(' ')[0]}` : ''}! How can I help?
                  </p>
                  <p className="text-xs text-ink">
                    Tap a topic below or type your question.
                  </p>
                </div>
                <div className="space-y-1.5">
                  {quickTopics.map((topic, i) => (
                    <button
                      key={i}
                      data-testid={`help-topic-${i}`}
                      onClick={() => handleTopicClick(topic.query)}
                      className="w-full text-left px-3 py-2.5 rounded-lg border border-line hover:border-accent hover:bg-accent-tint-2 transition-colors text-sm text-ink-muted flex items-center justify-between group"
                    >
                      <span>{topic.label}</span>
                      <ChevronRight className="w-4 h-4 text-ink-soft group-hover:text-accent transition-colors" />
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Messages */}
            {messages.map((msg, i) => (
              <div key={i}>
                {msg.type === "user" ? (
                  <div className="flex justify-end">
                    <div className="bg-ink text-white px-3 py-2 rounded-xl rounded-br-sm text-sm max-w-[85%]">
                      {msg.text}
                    </div>
                  </div>
                ) : msg.results ? (
                  <div className="space-y-2">
                    {msg.results.map((faq, j) => (
                      <div key={j} className="bg-app border border-line rounded-xl p-3">
                        <p className="text-xs font-semibold text-accent mb-1">{faq.category}</p>
                        <p className="text-sm font-medium text-ink mb-1.5">{faq.question}</p>
                        <p
                          className="text-sm text-ink-muted leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: formatAnswer(faq.answer) }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-app border border-line rounded-xl p-3">
                    <p
                      className="text-sm text-ink-muted leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: formatAnswer(msg.text) }}
                    />
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="flex-shrink-0 border-t border-line p-3">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                data-testid="help-bot-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your question..."
                className="flex-1 text-sm border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent"
              />
              <button
                data-testid="help-bot-send"
                onClick={() => handleSearch()}
                disabled={!query.trim()}
                className="bg-ink hover:bg-ink-hover disabled:bg-line-strong text-white p-2 rounded-lg transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
